import { Sparkles } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";

import { useTourContext } from "../context/tour-context";

// AIDEV-NOTE: Welcome modal shown before starting a tour
// Allows user to take the tour or skip it

interface WelcomeModalProps {
    /** ID of the tour to start when user clicks "Take Tour" */
    tourId: string;
}

export function WelcomeModal({ tourId }: WelcomeModalProps) {
    const {
        isWelcomeModalOpenForTour,
        closeWelcomeModal,
        startTour,
        skipTour,
        getTourById,
        isActive,
    } = useTourContext();

    // Get tour config by ID (not currentTour, which is null before tour starts)
    const tour = getTourById(tourId);
    const welcomeConfig = tour?.welcome;

    const handleTakeTour = () => {
        startTour(tourId);
    };

    const handleSkip = () => {
        skipTour(tourId); // Pass tourId explicitly since tour hasn't started yet
        closeWelcomeModal();
    };

    if (!welcomeConfig) {
        console.warn(`[WelcomeModal] Tour config not found for: ${tourId}`);
        return null;
    }

    // IMPORTANT: Don't render modal at all if tour is active
    if (isActive) {
        return null;
    }

    return (
        <Dialog
            analyticsId={`${tourId}-welcome-modal`}
            open={isWelcomeModalOpenForTour(tourId)}
            onOpenChange={(open) => {
                if (!open) {
                    handleSkip();
                }
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <div className="mb-4 flex justify-center">
                        <div className="rounded-full bg-brand-forest-100 p-4">
                            <Sparkles className="size-8 text-brand-forest-700" />
                        </div>
                    </div>
                    <DialogTitle className="text-center text-xl">
                        {welcomeConfig.title}
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        {welcomeConfig.description}
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-center">
                    <Button
                        variant="outline"
                        onClick={handleSkip}
                        className="border-brand-forest-200 text-brand-forest-700 hover:bg-brand-forest-50 hover:text-brand-forest-800 sm:order-1"
                    >
                        {welcomeConfig.skipButtonText || "Skip"}
                    </Button>
                    <Button
                        onClick={handleTakeTour}
                        variant="solidForest"
                        className="gap-2 sm:order-2"
                    >
                        <Sparkles className="size-4" />
                        {welcomeConfig.startButtonText || "Take Tour"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
